import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Plus,
  Edit2,
  Trash2,
  Shield,
  User,
  UserPlus,
  ArrowLeft,
  Users,
  Search,
  Filter,
  AlertTriangle,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { CargoModal } from '../../../components/modals/CargoModal';
import { api } from '../../../services/api';

interface GrupoCargo {
  id: string;
  id_grupo: string;
  cargo: string;
  descricao: string;
  admin: boolean;
  responsavel: boolean;
  ordem: number;
  criado_em: string;
  alterado_em: string;
  grupo?: {
    id: string;
    grupo: string;
    descricao: string;
  };
}

export function GerenciarCargos() {
  const { grupoId } = useParams<{ grupoId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [cargos, setCargos] = useState<GrupoCargo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAdmin, setFilterAdmin] = useState<'all' | 'admin' | 'member'>('all');

  // Modal states
  const [cargoModalOpen, setCargoModalOpen] = useState(false);
  const [cargoToEdit, setCargoToEdit] = useState<GrupoCargo | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cargoToDelete, setCargoToDelete] = useState<GrupoCargo | null>(null);

  useEffect(() => {
    if (grupoId) {
      loadCargos();
    }
  }, [grupoId]);

  const loadCargos = async () => {
    if (!grupoId) return;

    setLoading(true);
    try {
      const response = await api.get(`/grupos-cargos/grupo/${grupoId}`);
      setCargos(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar cargos:', error);
      enqueueSnackbar('Erro ao carregar cargos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (cargo: GrupoCargo) => {
    setCargoToEdit(cargo);
    setCargoModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setCargoToEdit(null);
    setCargoModalOpen(true);
  };

  const handleCloseModal = () => {
    setCargoModalOpen(false);
    setCargoToEdit(null);
  };

  const handleModalSuccess = () => {
    loadCargos();
  };

  const handleDeleteClick = (cargo: GrupoCargo) => {
    setCargoToDelete(cargo);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!cargoToDelete) return;

    try {
      await api.delete(`/grupos-cargos/${cargoToDelete.id}`);
      enqueueSnackbar('Cargo excluído com sucesso!', { variant: 'success' });
      loadCargos();
    } catch (error: any) {
      console.error('Erro ao excluir cargo:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao excluir cargo',
        { variant: 'error' }
      );
    } finally {
      setDeleteConfirmOpen(false);
      setCargoToDelete(null);
    }
  };

  const moveCargo = async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === cargos.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newCargos = [...cargos];
    const [removed] = newCargos.splice(index, 1);
    newCargos.splice(newIndex, 0, removed);

    // Atualizar estado local imediatamente para feedback instantâneo
    setCargos(newCargos);

    try {
      // Preparar dados para o backend
      const ordens = newCargos.map((cargo, idx) => ({
        id: cargo.id,
        ordem: idx + 1
      }));

      await api.patch('/grupos-cargos/ordem', { ordens });
      enqueueSnackbar('Ordem atualizada com sucesso', { variant: 'success' });

      // Recarregar dados do servidor em background para garantir sincronização
      loadCargos();
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar ordem', { variant: 'error' });
      // Em caso de erro, recarregar para reverter
      loadCargos();
    }
  };

  const filteredCargos = cargos.filter(cargo => {
    const matchesSearch = cargo.cargo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cargo.descricao.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterAdmin === 'all' ||
                         (filterAdmin === 'admin' && cargo.admin) ||
                         (filterAdmin === 'member' && !cargo.admin);

    return matchesSearch && matchesFilter;
  });

  const adminCargosCount = cargos.filter(cargo => cargo.admin).length;
  const responsavelCargosCount = cargos.filter(cargo => cargo.responsavel).length;
  const memberCargosCount = cargos.filter(cargo => !cargo.admin && !cargo.responsavel).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando cargos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/configuracoes/grupos')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Gerenciar Cargos</h1>
              {cargos.length > 0 && cargos[0].grupo && (
                <p className="text-muted-foreground">Grupo: {cargos[0].grupo.grupo}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            <span>Novo Cargo</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total de Cargos</p>
              <p className="text-xl font-semibold text-foreground">{cargos.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-sm text-muted-foreground">Cargos Administrativos</p>
              <p className="text-xl font-semibold text-foreground">{adminCargosCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <UserPlus className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-muted-foreground">Cargos de Responsável</p>
              <p className="text-xl font-semibold text-foreground">{responsavelCargosCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <User className="w-8 h-8 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Cargos de Membro</p>
              <p className="text-xl font-semibold text-foreground">{memberCargosCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card p-4 rounded-lg shadow border mb-6">
        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <input
                type="text"
                placeholder="Buscar por nome ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-muted-foreground" />
            <select
              value={filterAdmin}
              onChange={(e) => setFilterAdmin(e.target.value as 'all' | 'admin' | 'member')}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Cargos</option>
              <option value="admin">Apenas Administrativos</option>
              <option value="member">Apenas Membros</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cargos List */}
      <div className="bg-card rounded-lg shadow border">
        {filteredCargos.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {searchTerm || filterAdmin !== 'all'
                ? 'Nenhum cargo encontrado'
                : 'Nenhum cargo cadastrado'
              }
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || filterAdmin !== 'all'
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando o primeiro cargo para este grupo'
              }
            </p>
            {(!searchTerm && filterAdmin === 'all') && (
              <button
                onClick={handleOpenCreateModal}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus size={20} />
                <span>Criar Primeiro Cargo</span>
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredCargos.map((cargo, index) => (
              <div key={cargo.id} className="p-4 hover:bg-muted transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {cargo.admin ? (
                        <Shield className="w-5 h-5 text-amber-500" />
                      ) : cargo.responsavel ? (
                        <UserPlus className="w-5 h-5 text-green-600" />
                      ) : (
                        <User className="w-5 h-5 text-muted-foreground" />
                      )}
                      <h3 className="text-lg font-medium text-foreground">{cargo.cargo}</h3>
                      <div className="flex space-x-2">
                        {cargo.admin && (
                          <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded-full">
                            Administrativo
                          </span>
                        )}
                        {cargo.responsavel && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 rounded-full">
                            Responsável
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-2">{cargo.descricao}</p>
                    <p className="text-sm text-muted-foreground">
                      Criado em {new Date(cargo.criado_em).toLocaleDateString('pt-BR')}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Botões de ordenação - só aparecem quando não há filtros ativos */}
                    {(!searchTerm && filterAdmin === 'all') && (
                      <>
                        <button
                          onClick={() => moveCargo(index, 'up')}
                          disabled={index === 0}
                          className="p-2 text-muted-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Mover para cima"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => moveCargo(index, 'down')}
                          disabled={index === cargos.length - 1}
                          className="p-2 text-muted-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Mover para baixo"
                        >
                          <ChevronDown size={16} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleOpenEditModal(cargo)}
                      className="p-2 text-blue-500 hover:bg-muted rounded-md transition-colors"
                      title="Editar cargo"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(cargo)}
                      className="p-2 text-red-500 hover:bg-muted dark:hover:bg-red-900/20 rounded-md transition-colors"
                      title="Excluir cargo"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CargoModal
        open={cargoModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        grupoId={grupoId!}
        cargoToEdit={cargoToEdit}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && cargoToDelete && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-foreground">Confirmar Exclusão</h2>
            </div>

            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir o cargo <strong>"{ cargoToDelete.cargo}"</strong>?
              Esta ação não pode ser desfeita.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
